import { request } from "@/api/client";
import { PATHS } from "@/api/constants";

import type {
  AffiliatePartner,
  ApproveAffiliatePartnerBody,
  ListAffiliatePartnersQuery,
  PaginatedAffiliatePartners,
  UpsertAffiliatePartnerBody,
} from "./types";

type ApiEnvelope<T> = { ok: boolean; message?: string; data: T; meta?: any };

function unwrap<T>(res: any): T {
  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    return (res as ApiEnvelope<T>).data;
  }
  return res as T;
}

function qs(params: Record<string, unknown>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    u.set(k, String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : "";
}

const BASE = PATHS.affiliatePartners.base;

export async function listAffiliatePartners(
  query: ListAffiliatePartnersQuery = {}
): Promise<PaginatedAffiliatePartners> {
  const safePage = Math.max(1, Number(query.page ?? 1));
  const safeLimit = Math.max(1, Math.min(100, Number(query.limit ?? 20)));
  const url = `${BASE}${qs({
    page: safePage,
    limit: safeLimit,
    q: query.q,
    status: query.status,
    companyName: query.companyName?.trim() || undefined,
    contact: query.contact?.trim() || undefined,
  })}`;

  const res = await request<
    PaginatedAffiliatePartners | (ApiEnvelope<AffiliatePartner[]> & { meta?: any })
  >({
    method: "GET",
    url,
  });

  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    const wrapped = res as ApiEnvelope<AffiliatePartner[]> & { meta?: any };
    return {
      items: Array.isArray(wrapped.data) ? wrapped.data : [],
      page: Number(wrapped.meta?.page ?? safePage),
      pageSize: Number(wrapped.meta?.pageSize ?? safeLimit),
      total: Number(wrapped.meta?.total ?? 0),
    };
  }

  return res as PaginatedAffiliatePartners;
}

export async function getAffiliatePartner(id: number | string) {
  const res = await request<AffiliatePartner | ApiEnvelope<AffiliatePartner>>({
    method: "GET",
    url: `${BASE}/${id}`,
  });
  return unwrap<AffiliatePartner>(res);
}

export async function createAffiliatePartner(body: UpsertAffiliatePartnerBody) {
  const res = await request<AffiliatePartner | ApiEnvelope<AffiliatePartner>>({
    method: "POST",
    url: BASE,
    data: body,
  });
  return unwrap<AffiliatePartner>(res);
}

export async function updateAffiliatePartner(
  id: number | string,
  body: UpsertAffiliatePartnerBody
) {
  const res = await request<AffiliatePartner | ApiEnvelope<AffiliatePartner>>({
    method: "PUT",
    url: `${BASE}/${id}`,
    data: body,
  });
  return unwrap<AffiliatePartner>(res);
}

export async function approveAffiliatePartner(
  id: number | string,
  body: ApproveAffiliatePartnerBody
) {
  const res = await request<AffiliatePartner | ApiEnvelope<AffiliatePartner>>({
    method: "POST",
    url: `${BASE}/${id}/approve`,
    data: body,
  });
  return unwrap<AffiliatePartner>(res);
}

export async function rejectAffiliatePartner(id: number | string) {
  const res = await request<AffiliatePartner | ApiEnvelope<AffiliatePartner>>({
    method: "POST",
    url: `${BASE}/${id}/reject`,
  });
  return unwrap<AffiliatePartner>(res);
}

export async function resendAffiliatePartnerPassword(id: number | string) {
  const res = await request<AffiliatePartner | ApiEnvelope<AffiliatePartner>>({
    method: "POST",
    url: `${BASE}/${id}/resend-password`,
  });
  return unwrap<AffiliatePartner>(res);
}

export async function resendAffiliatePartnerWelcomeEmail(id: number | string) {
  const res = await request<AffiliatePartner | ApiEnvelope<AffiliatePartner>>({
    method: "POST",
    url: `${BASE}/${id}/resend-welcome-email`,
  });
  return unwrap<AffiliatePartner>(res);
}
